import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { api, teamUsersApi } from '../api';

const MaintenanceTeamManagement = () => {
  const { user } = useContext(AuthContext);
  const [teamUsers, setTeamUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    first_name: '',
    last_name: ''
  });

  useEffect(() => {
    loadTeamUsers();
  }, []);

  const loadTeamUsers = async () => {
    try {
      const response = await teamUsersApi.getTeamUsers(user.hoa_id, 'maintenance');
      setTeamUsers(response.data.team_users || []);
    } catch (error) {
      console.error('Failed to load maintenance team:', error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await teamUsersApi.createTeamUser({
        ...newUser,
        role: 'maintenance'
      }, user.hoa_id);
      
      setNewUser({ email: '', first_name: '', last_name: '' });
      setShowAddForm(false);
      await loadTeamUsers();
      alert('Maintenance team member added successfully! They will need board approval before becoming active.');
    } catch (error) {
      console.error('Failed to add team member:', error);
      alert('Failed to add team member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const response = await teamUsersApi.bulkImportTeamUsers(file, 'maintenance', user.hoa_id);

      alert(`Import completed! ${response.data.imported_count} users imported and pending approval.`);
      if (response.data.errors.length > 0) {
        console.log('Import errors:', response.data.errors);
      }
      await loadTeamUsers();
    } catch (error) {
      console.error('Bulk import failed:', error);
      alert('Bulk import failed. Please try again.');
    }
  };

  const handleExport = async () => {
    try {
      const response = await teamUsersApi.exportTeamUsers(user.hoa_id, 'maintenance');

      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const approveUser = async (userId) => {
    try {
      await teamUsersApi.approveTeamUser(userId, user.hoa_id);
      await loadTeamUsers();
      alert('Team member approved successfully!');
    } catch (error) {
      console.error('Failed to approve user:', error);
      alert('Failed to approve user. Please try again.');
    }
  };

  const rejectUser = async (userId) => {
    try {
      await teamUsersApi.rejectTeamUser(userId, 'Rejected by board', user.hoa_id);
      await loadTeamUsers();
      alert('Team member rejected.');
    } catch (error) {
      console.error('Failed to reject user:', error);
      alert('Failed to reject user. Please try again.');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await teamUsersApi.updateTeamUser(userId, {
        is_active: !currentStatus
      }, user.hoa_id);
      await loadTeamUsers();
    } catch (error) {
      console.error('Failed to update user status:', error);
      alert('Failed to update user status.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Maintenance Team Management</h3>
        <p className="mt-1 text-sm text-gray-600">
          Manage maintenance team members who can receive and process maintenance requests.
        </p>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-4">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        >
          + Add Team Member
        </button>
        
        <label className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 cursor-pointer transition-colors">
          📤 Bulk Import CSV
          <input
            type="file"
            accept=".csv"
            onChange={handleBulkImport}
            className="hidden"
          />
        </label>
        
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          📥 Export CSV
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-md font-medium text-gray-900 mb-4">Add New Maintenance Team Member</h4>
          <form onSubmit={handleAddUser} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                required
                value={newUser.first_name}
                onChange={(e) => setNewUser(prev => ({ ...prev, first_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                required
                value={newUser.last_name}
                onChange={(e) => setNewUser(prev => ({ ...prev, last_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="md:col-span-3 flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add Member'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Team Members List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            Maintenance Team Members ({teamUsers.length})
          </h4>
          
          {teamUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">🔧</div>
              <p>No maintenance team members added yet</p>
              <p className="text-sm mt-1">Add team members to manage maintenance requests</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {teamUsers.map((member) => (
                    <tr key={member.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {member.first_name} {member.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {member.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          member.status === 'approved' && member.is_active
                            ? 'bg-green-100 text-green-800'
                            : member.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : member.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {member.status === 'approved' && member.is_active
                            ? 'Active'
                            : member.status === 'pending'
                            ? 'Pending Approval'
                            : member.status === 'rejected'
                            ? 'Rejected'
                            : 'Inactive'
                          }
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(member.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-wrap gap-2">
                          {member.status === 'pending' ? (
                            <>
                              <button
                                onClick={() => approveUser(member.id)}
                                className="text-green-600 hover:text-green-900 px-2 py-1 bg-green-50 rounded text-xs"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => rejectUser(member.id)}
                                className="text-red-600 hover:text-red-900 px-2 py-1 bg-red-50 rounded text-xs"
                              >
                                Reject
                              </button>
                            </>
                          ) : member.status === 'approved' ? (
                            <button
                              onClick={() => toggleUserStatus(member.id, member.is_active)}
                              className={`px-2 py-1 rounded text-xs ${
                                member.is_active 
                                  ? 'text-red-600 hover:text-red-900 bg-red-50'
                                  : 'text-green-600 hover:text-green-900 bg-green-50'
                              }`}
                            >
                              {member.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          ) : member.status === 'rejected' ? (
                            <button
                              onClick={() => approveUser(member.id)}
                              className="text-green-600 hover:text-green-900 px-2 py-1 bg-green-50 rounded text-xs"
                            >
                              Re-approve
                            </button>
                          ) : (
                            <span className="text-gray-500 text-xs">No actions</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* CSV Import Instructions */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">CSV Import Format</h4>
        <p className="text-sm text-blue-700 mb-2">
          To bulk import maintenance team members, create a CSV file with these columns:
        </p>
        <code className="text-xs bg-blue-100 px-2 py-1 rounded text-blue-800">
          email,first_name,last_name
        </code>
        <p className="text-xs text-blue-600 mt-2">
          Example: john.doe@maintenance.com,John,Doe
        </p>
      </div>
    </div>
  );
};

export default MaintenanceTeamManagement;